import { FormattedMessage } from "react-intl";
import type { ReactNode } from "react";

import { type UseFormReturn } from "common/core/form";
import Button from "common/core/button";
import { CALL_STATES } from "constants/dial_out";
import { PhoneNumberInput } from "common/core/form/phone-number";

import TechStatusInfo, { MESSAGING } from "../tech_status_info";
import Styles from "./phone.module.scss";

type PhoneControlProps = {
  form: UseFormReturn<{ phoneNumber: string }>;
  callState: (typeof CALL_STATES)[keyof typeof CALL_STATES];
  onDial: () => void;
  onHangUp: () => void;
  showNotaryConnectionWarn: boolean;
  label?: ReactNode;
};

function PhoneControl({
  form,
  callState,
  onDial,
  onHangUp,
  showNotaryConnectionWarn,
  label,
}: PhoneControlProps) {
  const disableInput = callState === CALL_STATES.CONNECTING || callState === CALL_STATES.CONNECTED;

  const button = (() => {
    switch (callState) {
      case CALL_STATES.CONNECTED:
        return (
          <Button variant="secondary" buttonColor="danger" fullwidth onClick={onHangUp}>
            <FormattedMessage
              id="79104d7e-fe35-49f6-b85a-b39eb643202a"
              defaultMessage="DISCONNECT AUDIO"
            />
          </Button>
        );
      case CALL_STATES.CONNECTING:
        return null;
      case CALL_STATES.FAILED:
        return (
          <Button
            variant="secondary"
            buttonColor="action"
            fullwidth
            onClick={onDial}
            disabled={!form.formState.isValid}
          >
            <FormattedMessage id="486b41f6-1670-44be-a3c0-05bd0a1d6297" defaultMessage="REDIAL" />
          </Button>
        );
      case CALL_STATES.READY_FOR_CALL:
      default:
        return (
          <Button
            variant="secondary"
            buttonColor="action"
            fullwidth
            onClick={onDial}
            disabled={!form.formState.isValid}
          >
            <FormattedMessage
              id="6826547b-9024-4bbb-93a0-dde08b26a57b"
              defaultMessage="Dial in for Audio"
            />
          </Button>
        );
    }
  })();

  const callStatusInfo = (() => {
    if (showNotaryConnectionWarn) {
      return (
        <TechStatusInfo
          detailsIcon="exclamation-triangle"
          detailsIconVariant="warning"
          detailsMainText={MESSAGING.notaryWarnMain}
          detailsSubText={MESSAGING.notaryWarnSub}
          isAlert
        />
      );
    }

    switch (callState) {
      case CALL_STATES.CONNECTING:
        return (
          <TechStatusInfo
            detailsIcon="arrow-clockwise"
            detailsIconVariant="connecting"
            detailsMainText={MESSAGING.phoneConnectingMain}
            detailsSubText={MESSAGING.phoneConnectingSub}
          />
        );
      case CALL_STATES.CONNECTED:
        return (
          <TechStatusInfo
            detailsIcon="check-circle"
            detailsIconVariant="success"
            detailsMainText={MESSAGING.phoneConnectedMain}
          />
        );
      case CALL_STATES.FAILED:
        return (
          <TechStatusInfo
            detailsIcon="exclamation-hexagon"
            detailsIconVariant="error"
            detailsMainText={MESSAGING.phoneFailedMain}
            detailsSubText={MESSAGING.phoneFailedSub}
          />
        );
      default:
        return null;
    }
  })();

  return (
    <div className={Styles.phoneInputContainer}>
      <PhoneNumberInput
        control={form.control}
        name="phoneNumber"
        label={label}
        isRequired
        disabled={disableInput}
        data-automation-id="phone-number-input"
      />
      <div className={Styles.button}>{button}</div>
      {callStatusInfo}
    </div>
  );
}

export default PhoneControl;
